<template>
  <v-dialog
    v-bind="$attrs"
    max-width="400"
    :model-value="modelValue"
    :scrollable="false"
    @click:outside="
      $emit('reject-delete');
      isActionConfirmed = false;
    "
  >
    <v-card>
      <v-card-item v-if="header">
        <v-card-title>{{ header }}</v-card-title>
      </v-card-item>

      <v-card-text v-if="message || showCheckboxField || showConfirmField">
        <div class="text-body-2" v-html="message"></div>

        <v-checkbox
          v-if="showCheckboxField"
          v-model="isActionConfirmed"
          class="mt-4"
          hide-details
          density="compact"
          data-test="s-delete-confirmation-checkbox"
        >
          <template #label>
            <span class="text-body-2 ml-2">{{ $t(checkboxMessage) }}</span>
          </template>
        </v-checkbox>

        <v-form ref="deleteForm" v-model="valid" @submit.prevent="handleDelete">
          <v-text-field
            v-if="showConfirmField"
            class="mt-4"
            variant="outlined"
            hide-details
            flat
            required
            :label="
              $t('common.common_components.s_delete_confirmation.delete_label', {
                deleteText: $t(deleteConfirmInputText)
              })
            "
            :rules="[(v) => (v && v.toLowerCase() === $t(deleteConfirmInputText) ? true : '')]"
            data-test="s-delete-confirmation-text-field"
          />
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-row no-gutters justify="end">
          <s-btn class="mr-2" cancel @click="cancelDelete" data-test="s-delete-confirmation-cancel-btn">
            {{ $t('common.buttons.cancel') }}
          </s-btn>
          <s-btn
            primary
            :color="isDeleteAction ? 'error' : 'primary'"
            @click="confirmDelete"
            :disabled="disableDelete"
            data-test="s-delete-confirmation-apply-btn"
          >
            {{ $t(deleteText) }}
          </s-btn>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * Modal asking for a confirmation before delete.
 *
 * @component common/components/s-delete-confirmation
 * @vueprop {String} [textMessage] Message to display inside the modal
 * @vueprop {Boolean} [showConfirmField] Show confirmation field for deletion
 * @vueprop {Boolean} [disableConfirm=false] Disable confirmation
 * @vueprop {String} [heading] Contains title
 * @vueprop {String} [deleteText] Contains text for confirm button
 *
 * @emits confirm-delete  The user has confirmed the deletion of data
 *
 * @emits reject-delete The user reject the action
 */

export default {
  props: {
    textMessage: { type: String, default: null },
    showConfirmField: Boolean,
    disableConfirm: { type: Boolean, default: false },
    modelValue: Boolean,
    heading: { type: String, default: null },
    deleteText: { type: String, default: 'common.label.delete' },
    title: { type: String, default: null },
    featureName: { type: String, default: null },
    showCheckboxField: { type: Boolean, default: false },
    deleteConfirmInputText: {
      type: String,
      default: 'common.common_components.s_delete_confirmation.delete_text'
    },
    checkboxMessage: {
      type: String,
      default: 'common.label.checkbox_message'
    },
    confirmMessage: {
      type: String,
      default: 'common.dynamic_labels.confirm_delete'
    },
    headerMessage: {
      type: String,
      default: 'common.dynamic_labels.delete_feature'
    },
    isDeleteAction: {
      type: Boolean,
      default: true
    }
  },

  emits: ['confirm-delete', 'reject-delete'],

  data() {
    return {
      valid: false,
      isActionConfirmed: false
    };
  },
  computed: {
    message() {
      if (this.title && this.featureName) {
        return this.$t(this.confirmMessage, {
          title: this.$t(this.title),
          feature: this.$t(this.featureName)
        });
      }
      return this.textMessage && this.$t(this.textMessage);
    },
    header() {
      if (this.heading) return this.$t(this.heading);

      if (this.featureName) {
        return this.$t(this.headerMessage, {
          feature: this.$t(this.featureName)
        });
      }
      return null;
    },

    disableDelete() {
      return (
        this.disableConfirm ||
        (this.showCheckboxField && !this.isActionConfirmed) ||
        (this.showConfirmField ? !this.valid : false)
      );
    }
  },
  methods: {
    handleDelete(event) {
      if (event) event.preventDefault();
    },

    confirmDelete() {
      this.$emit('confirm-delete');
      this.isActionConfirmed = false;
    },

    cancelDelete() {
      this.$emit('reject-delete');
      this.isActionConfirmed = false;
    }
  }
};
</script>
